import { useState, useCallback, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plane, Home, MessageSquare, User, Mail, Phone, Send } from "lucide-react";
import TravelerSearchForm from "./TravelerSearchForm";
import BookingForm from "./BookingForm";
import ArtDecoFrame from "./ArtDecoFrame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import HCaptcha from "@hcaptcha/react-hcaptcha";

type ProfileType = "traveler" | "owner" | "other";

export default function HomeLeadSection() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const ts = t.travelerSearch;
  const tp = (t as any).contactProfiles;
  const [profile, setProfile] = useState<ProfileType>("other");
  const [progress, setProgress] = useState(0);

  // Pre-select profile from URL param ?profile=owner
  useEffect(() => {
    const param = searchParams.get("profile");
    if (param === "owner" || param === "traveler" || param === "other") {
      setProfile(param);
    }
  }, [searchParams]);

  // Other form fields
  const [otherName, setOtherName] = useState("");
  const [otherEmail, setOtherEmail] = useState("");
  const [otherPhone, setOtherPhone] = useState("");
  const [otherMessage, setOtherMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);

  const handleProgressChange = useCallback((newProgress: number) => {
    setProgress(newProgress);
  }, []);

  // Calculate progress for "other" profile
  const getOtherProgress = () => {
    const filled = [otherName, otherEmail, otherMessage].filter(Boolean).length;
    return Math.round((filled / 3) * 100);
  };

  const currentProgress = profile === "other" ? getOtherProgress() : progress;
  const perimeterProgress = currentProgress;

  const profiles: { key: ProfileType; icon: typeof Plane; label: string; desc: string }[] = [
    {
      key: "owner",
      icon: Home,
      label: tp?.owner || "Propriétaire",
      desc: tp?.ownerDesc || "Estimation gratuite de mes revenus locatifs potentiels",
    },
    {
      key: "traveler",
      icon: Plane,
      label: tp?.traveler || "Voyageur",
      desc: tp?.travelerDesc || "Je cherche un hébergement",
    },
    {
      key: "other",
      icon: MessageSquare,
      label: tp?.other || "Autre",
      desc: tp?.otherDesc || "Autre demande",
    },
  ];

  const benefits: Record<string, string[]> = {
    traveler: [
      ts?.benefits?.response24h || "Réponse sous 24h",
      ts?.benefits?.personalizedSelection || "Sélection personnalisée",
      ts?.benefits?.noBookingFees || "Sans frais de réservation",
      ts?.benefits?.dedicatedAdvisor || "Conseiller dédié",
    ],
    owner: [
      t.estimation?.benefits?.freeEstimate || "Estimation gratuite revenus locatifs",
      t.estimation?.benefits?.revenueOptimization || "Optimisation des revenus",
      t.estimation?.benefits?.turnkeyManagement || "Gestion clé en main",
      t.estimation?.benefits?.insuranceIncluded || "Assurance incluse",
    ],
    other: tp?.otherBenefits || [
      "Réponse sous 24h",
      "Équipe à votre écoute",
      "Sans engagement",
    ],
  };

  const handleOtherSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!otherName.trim() || !otherEmail.trim() || !otherMessage.trim()) {
      toast.error(t.contact?.form?.error || "Veuillez remplir tous les champs obligatoires");
      setIsSubmitting(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(otherEmail)) {
      toast.error(t.contact?.form?.error || "Veuillez entrer une adresse email valide");
      setIsSubmitting(false);
      return;
    }

    if (!captchaToken) {
      toast.error(t.contact?.form?.error || "Veuillez compléter le captcha");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "9b248f28-7389-4c29-a486-ab80ea310230",
          cc: "esl@outlook.fr",
          subject: "[Contact] Nouveau message de contact",
          from_name: otherName,
          name: otherName,
          email: otherEmail,
          phone: otherPhone || "Non renseigné",
          message: otherMessage,
          profil: "Autre",
          "h-captcha-response": captchaToken,
        }),
      });

      const result = await response.json();
      if (result.success) {
        toast.success(t.contact?.form?.success || "Message envoyé avec succès !");
        setOtherName("");
        setOtherEmail("");
        setOtherPhone("");
        setOtherMessage("");
        setCaptchaToken(null);
        captchaRef.current?.resetCaptcha();
      } else {
        toast.error(t.contact?.form?.error || "Erreur lors de l'envoi.");
      }
    } catch (error) {
      console.error("Erreur Web3Forms:", error);
      toast.error(t.contact?.form?.error || "Erreur de connexion.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative group">
      {/* Progress border container */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-0 left-0 h-[2px] bg-gold"
          initial={{ width: "0%" }}
          animate={{ width: `${Math.min(perimeterProgress * 4, 100)}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
        <motion.div
          className="absolute top-0 right-0 w-[2px] bg-gold"
          initial={{ height: "0%" }}
          animate={{ height: `${Math.max(0, Math.min((perimeterProgress - 25) * 4, 100))}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
        <motion.div
          className="absolute bottom-0 right-0 h-[2px] bg-gold"
          initial={{ width: "0%" }}
          animate={{ width: `${Math.max(0, Math.min((perimeterProgress - 50) * 4, 100))}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-[2px] bg-gold"
          initial={{ height: "0%" }}
          animate={{ height: `${Math.max(0, Math.min((perimeterProgress - 75) * 4, 100))}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </div>

      <ArtDecoFrame mode="inside" />

      {/* Progress indicator */}
      {profile && (
        <div className="absolute -top-6 right-0 flex items-center gap-2">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">
            {ts?.progress || "Progression"}
          </span>
          <motion.span
            key={Math.round(currentProgress)}
            initial={{ scale: 1.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-sm font-medium text-gold"
          >
            {Math.round(currentProgress)}%
          </motion.span>
        </div>
      )}

      {/* Content */}
      <div className="bg-card border border-border p-8 md:p-10 lg:p-12">
        {/* Profile Selector */}
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{tp?.selectProfile || "Cliquez pour choisir votre profil"}</p>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
          {profiles.map(({ key, icon: Icon, label, desc }) => (
            <motion.button
              key={key}
              type="button"
              onClick={() => {
                setProfile(key);
                setProgress(0);
              }}
              whileHover={{ scale: 1.03, y: -3 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              className={`p-3 sm:p-5 md:p-6 border-2 text-center transition-all duration-300 cursor-pointer flex flex-col items-center sm:items-start sm:text-left ${
                profile === key
                  ? "border-gold bg-gold/15 shadow-[0_0_24px_-5px_hsl(var(--gold)/0.35)]"
                  : "border-gold/30 hover:border-gold/60 hover:bg-gold/10 bg-gold/5 shadow-sm hover:shadow-md"
              }`}
            >
              <Icon
                className={`h-5 w-5 sm:h-7 sm:w-7 mb-1.5 sm:mb-2 transition-colors duration-300 ${
                  profile === key
                    ? "text-gold"
                    : "text-gold/60"
                }`}
              />
              <p
                className={`text-[11px] sm:text-base font-semibold transition-colors duration-300 leading-tight ${
                  profile === key ? "text-gold" : "text-foreground/90"
                }`}
              >
                {label}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">{desc}</p>
            </motion.button>
          ))}
        </div>

        {/* Dynamic content */}
        <AnimatePresence mode="wait">
          {profile && (
            <motion.div
              key={profile}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >

              {/* Form */}
              {profile === "traveler" && (
                <TravelerSearchForm onProgressChange={handleProgressChange} />
              )}
              {profile === "owner" && (
                <BookingForm onProgressChange={handleProgressChange} />
              )}
              {profile === "other" && (
                <form onSubmit={handleOtherSubmit} className="relative bg-card/80 backdrop-blur-sm border border-border/50 p-4 md:p-5 space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-[1px] bg-border" />
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">{tp?.yourCoordinates || "Vos Coordonnées"}</span>
                      <div className="flex-1 h-[1px] bg-border" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
                          <User className="w-3.5 h-3.5" /> {t.contact?.form?.name || "Nom complet"} *
                        </label>
                        <Input
                          placeholder={t.contact?.form?.name || "Votre nom complet"}
                          value={otherName}
                          onChange={(e) => setOtherName(e.target.value)}
                          maxLength={100}
                          className="h-9 text-sm bg-background/50 border-border/50"
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
                          <Mail className="w-3.5 h-3.5" /> {t.contact?.form?.email || "Email"} *
                        </label>
                        <Input
                          type="email"
                          placeholder={t.contact?.form?.email || "votre@email.com"}
                          value={otherEmail}
                          onChange={(e) => setOtherEmail(e.target.value)}
                          maxLength={255}
                          className="h-9 text-sm bg-background/50 border-border/50"
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
                          <Phone className="w-3.5 h-3.5" /> {t.contact?.form?.phone || "Téléphone"}
                        </label>
                        <Input
                          type="tel"
                          placeholder="+33 6 00 00 00 00"
                          value={otherPhone}
                          onChange={(e) => setOtherPhone(e.target.value)}
                          maxLength={20}
                          className="h-9 text-sm bg-background/50 border-border/50"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
                        <MessageSquare className="w-3.5 h-3.5" /> {t.contact?.form?.message || "Votre message"} *
                      </label>
                      <Textarea
                        placeholder={tp?.howCanWeHelp || "Comment pouvons-nous vous aider ?"}
                        value={otherMessage}
                        onChange={(e) => setOtherMessage(e.target.value)}
                        maxLength={1000}
                        className="text-sm bg-background/50 border-border/50 min-h-[80px]"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-center transform scale-[0.85] origin-center -my-2">
                    <HCaptcha
                      ref={captchaRef}
                      sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
                      reCaptchaCompat={false}
                      size="normal"
                      theme="dark"
                      onVerify={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-10 bg-foreground text-background hover:bg-foreground/90 uppercase tracking-wider text-xs"
                    disabled={isSubmitting || !captchaToken}
                  >
                    {isSubmitting ? (
                      t.contact?.form?.sending || "Envoi en cours..."
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        {tp?.sendTheMessage || "Envoyer le message"}
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}
